import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api.client';
import { queryKeys } from '../lib/queryKeys';
import type { CaseDetail } from '@lnr-v2/types';

interface UseCaseReturn {
  case: CaseDetail | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook for fetching a single case with React Query
 *
 * Benefits:
 * - Automatic caching and background refetching
 * - Consistent error handling
 * - Better integration with React Query DevTools
 * - Cache is automatically updated when case is edited via useUpdateCase
 * - Matches the pattern used across the application
 */
export const useCase = (id?: string): UseCaseReturn => {
  const query = useQuery({
    queryKey: queryKeys.cases.detail(id || ''),
    queryFn: async (): Promise<CaseDetail> => {
      if (!id) {
        throw new Error('Case ID is required');
      }

      const response = await apiClient.get(`cases/id/${id}`);
      const result = await response.json<{
        success: boolean;
        data: CaseDetail;
      }>();

      if (result.success && result.data) {
        return result.data;
      }
      throw new Error('Failed to fetch case');
    },
    // Only run query if id is provided
    enabled: !!id,
    // Disable automatic refetching in background for detail views
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Keep case data in cache for 10 minutes after component unmounts
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });

  // Convert query error to Error type for backwards compatibility
  const error = query.error ? (query.error as Error) : null;

  // Wrapper for refetch to maintain backwards compatibility
  const refetch = async () => {
    await query.refetch();
  };

  return {
    case: query.data || null,
    loading: query.isLoading,
    error,
    refetch,
  };
};
